import { Button, HStack, TabPanel, Text, useToast, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { PasswordInputField } from 'src/components/PasswordInputField';
import { changePasswordFormSchema } from './schemas/changePasswordFormSchema';
import { ChangePasswordFormData } from './schemas/types';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { PasswordCheck } from '@/components/PasswordCheck';
import { useAuthContext } from '@/contexts/AuthContext';
import { useGetConfig } from '@/services/queries/admin-config';
import { usePutDashboardUserPasswordMutation } from '@/services/queries/dashboard-user';
import { PasswordRegex } from '@/model';
import { useCallback, useEffect, useState } from 'react';
import { PutDashboardUserPasswordRequest } from '@/services/request/dashboard-user';
import { useTranslation } from 'react-i18next';

export const SecurityTabContent = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { authenticationInfo } = useAuthContext();

  const [regexes, setRegexes] = useState<PasswordRegex[]>([]);

  const { data: configData, isLoading: isLoadingConfig } = useGetConfig();
  const putDashboardUserPasswordMutation = usePutDashboardUserPasswordMutation();

  const {
    reset,
    handleSubmit,
    register,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordFormSchema),
  });

  const password = watch('newPassword');
  const confirmPassowrd = watch('passwordConfirmation');

  const onSubmit: SubmitHandler<ChangePasswordFormData> = useCallback(
    data => {
      if (!authenticationInfo || !authenticationInfo.uuid) return;
      const payload: PutDashboardUserPasswordRequest = {
        old_password: data.currentPassword,
        new_password: data.newPassword,
      };

      putDashboardUserPasswordMutation.mutate(
        { id: authenticationInfo?.uuid, payload },
        {
          onSuccess: () => {
            reset();
            toast({
              status: 'success',
              title: t('pages.profile.components.security_tab_content.security_tab_content_success_toast'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) || t('pages.profile.components.security_tab_content.security_tab_content_error_toast'),
            });
          },
        },
      );
    },
    [authenticationInfo, putDashboardUserPasswordMutation, reset, toast, t],
  );

  const onHandleValidate = useCallback(() => {
    const regexes = configData?.dashboard_regex || [];

    const m = regexes.map(obj => new RegExp(obj.regex).test(password));

    return m.every(v => v) && password === confirmPassowrd;
  }, [configData?.dashboard_regex, confirmPassowrd, password]);

  useEffect(() => {
    if (configData) {
      setRegexes(configData?.dashboard_regex || []);
    }
  }, [configData]);

  return (
    <TabPanel>
      <VStack as='form' maxWidth='450px' align='stretch' gap='kilo' onSubmit={handleSubmit(onSubmit)} noValidate>
        <VStack align='start' gap='xxsmall' mb={4}>
          <HStack align='center' mb='kilo'>
            <ChakraFeatherIcon name='lock' />
            <Text variant='tabContentTitle' m='0'>
              {t('pages.profile.components.security_tab_content.security_tab_content_title')}
            </Text>
          </HStack>

          <PasswordInputField
            label={t('pages.profile.components.security_tab_content.security_tab_content_current_password_label')}
            rootProps={{
              isRequired: true,
              isInvalid: !!errors.currentPassword,
              errorMessage: errors.currentPassword?.message,
            }}
            {...register('currentPassword')}
          />
        </VStack>

        <VStack gap='kilo' align='start'>
          <PasswordInputField
            label={t('pages.profile.components.security_tab_content.security_tab_content_new_password_label')}
            rootProps={{
              isRequired: true,
              isInvalid: !!errors.newPassword,
              errorMessage: errors.newPassword?.message,
            }}
            {...register('newPassword')}
            mb={4}
          />
          <PasswordInputField
            label={t('pages.profile.components.security_tab_content.security_tab_content_confirm_password_label')}
            rootProps={{
              isRequired: true,
              isInvalid: !!errors.passwordConfirmation,
              errorMessage: errors.passwordConfirmation?.message,
            }}
            {...register('passwordConfirmation')}
          />

          <PasswordCheck password={password} regexes={regexes} />

          <Button mt='kilo' minW='200px' type='submit' isLoading={isSubmitting} disabled={!onHandleValidate()}>
            {t('pages.profile.components.security_tab_content.security_tab_content_submit_button')}
          </Button>
        </VStack>
      </VStack>
      <CenteredSpinner isLoading={isLoadingConfig || putDashboardUserPasswordMutation.isLoading} />
    </TabPanel>
  );
};
